/**
 * Turning a signed URL into something an `<img>` can render.
 *
 * The gallery used to be handed a URL and put it straight in `src`. It cannot
 * any more: what sits behind that URL is ciphertext, and a browser asked to
 * decode it draws a broken-image icon. So the fetch happens here, the bytes are
 * opened with the file key, and what goes into `src` is an object URL over the
 * plaintext.
 *
 * ── Why object URLs, and what they cost ──
 *
 * `URL.createObjectURL` hands back a string that stays valid until it is
 * revoked, and the blob behind it is held in memory the whole time. That is the
 * trap: a gallery scrolled through a few hundred photographs would accumulate
 * every one of them and never give the memory back, because dropping the string
 * does nothing — the blob is anchored by the URL, not by references to it.
 *
 * Hence the cache below is bounded and evicts by revoking. The alternative — a
 * data URL — has no lifetime problem but base64 costs a third more memory and
 * cannot be streamed, so it is worse on both counts.
 *
 * ── Why entries are counted ──
 *
 * A bound alone is not enough. Eviction revokes, and revoking a URL that an
 * `<img>` is currently showing blanks the picture on screen — a defect that
 * would appear only under scroll, only on large previews, and only sometimes.
 * So callers retain what they display and release it when they stop, and
 * nothing retained is ever evicted. The bound then applies to what is merely
 * cached.
 *
 * ── Why keyed by URL ──
 *
 * The signed URL is quantised to an hour server-side, so the same object yields
 * the same string for that hour: a natural cache key that changes exactly when
 * the underlying grant does. Keying by file id instead would serve a stale
 * decryption after a variant was replaced.
 */

import { MediaCryptoError, decrypt } from "@fc/core";
import {
  hasGroupKey,
  isUnlocked,
  openFileKey,
  subscribeKeyring,
} from "./keyring.js";

/**
 * How many decrypted objects to keep once nothing is displaying them.
 *
 * A screen of tiles is roughly forty; three screens of scrollback is what makes
 * scrolling back up feel instant. At thumbnail sizes that is a few megabytes.
 * Previews are far larger, which is why bytes are bounded as well.
 */
const MAX_ENTRIES = 120;

/**
 * A ceiling in bytes, scaled to the device.
 *
 * One preview can outweigh a hundred thumbnails, so a count alone is not a
 * bound. A fixed 96 MB was fine on a laptop and reckless on the phones this is
 * mostly used from: a 2 GB Android gives a tab a few hundred megabytes in
 * total, and filling a third of it with photographs nobody is currently looking
 * at is how a gallery reloads itself mid-scroll.
 *
 * Roughly a twentieth of what the device admits to having, held between a
 * floor that still fits a screen of previews and the old ceiling. Chrome
 * reports `deviceMemory` rounded and capped at 8; Safari and Firefox report
 * nothing at all, and the fallback assumes a modest phone rather than the
 * machine this was written on.
 */
const MAX_BYTES = (() => {
  const reported =
    typeof navigator === "undefined"
      ? undefined
      : (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  const gigabytes = reported ?? 4;
  const budget = (gigabytes * 1024 * 1024 * 1024) / 20;
  return Math.min(Math.max(budget, 24 * 1024 * 1024), 96 * 1024 * 1024);
})();

interface Entry {
  /** The signed URL, which is this entry's key in `cache`. */
  sourceUrl: string;
  objectUrl: string;
  bytes: number;
  /** How many callers are displaying it. Never evicted while above zero. */
  holders: number;
}

/** Insertion-ordered, which `Map` guarantees — so the oldest key comes first. */
const cache = new Map<string, Entry>();

/**
 * The same entries, found by what a caller is actually holding.
 *
 * Callers are handed an object URL and give an object URL back; the cache is
 * keyed by the signed URL it came from. Without this second index `release`
 * looks up a key that is never in the map, finds nothing, and silently does
 * nothing — every photograph then stays pinned for the life of the tab, and
 * nothing anywhere reports it.
 */
const byObjectUrl = new Map<string, Entry>();

let heldBytes = 0;

/** In-flight work, so a tile rendered twice decrypts once. */
const inFlight = new Map<string, Promise<string>>();

/** Thrown when the bytes are reachable but the keyring is not open. */
export class LockedError extends Error {
  constructor() {
    super("The keyring is locked. Sign in again to see photographs.");
    this.name = "LockedError";
  }
}

/**
 * Thrown when the request to object storage never completed.
 *
 * Almost always CORS. The browser refuses to hand the response to script and
 * `fetch` rejects with a bare `TypeError: Failed to fetch` — no status, no body,
 * nothing to inspect — so without naming it here the interface can only say "no
 * preview", which points at the photograph. The photograph is fine.
 *
 * It earns its own type because of how the failure presents. Only *sealed*
 * files are fetched; an unsealed one is handed straight to an `<img>`, which
 * needs no CORS at all. A bucket missing the site's origin therefore looks
 * exactly like "the encrypted groups are broken and the old ones are fine",
 * which sends you hunting through the encryption, where nothing is wrong.
 */
export class StorageUnreachableError extends Error {
  constructor() {
    super(
      "The browser could not reach photo storage. It may not allow this site.",
    );
    this.name = "StorageUnreachableError";
  }
}

function revoke(key: string): void {
  const entry = cache.get(key);
  if (!entry) return;
  cache.delete(key);
  byObjectUrl.delete(entry.objectUrl);
  heldBytes -= entry.bytes;
  // Without this the blob stays alive for the lifetime of the document.
  URL.revokeObjectURL(entry.objectUrl);
}

function evictWhileOver(): void {
  if (cache.size <= MAX_ENTRIES && heldBytes <= MAX_BYTES) return;

  for (const [key, entry] of cache) {
    if (cache.size <= MAX_ENTRIES && heldBytes <= MAX_BYTES) return;
    /*
     * Retained entries are on screen. Skipping them can leave the cache above
     * its bound, which is the right trade: the bound is a memory preference,
     * and a tile that goes blank mid-scroll is a visible defect.
     */
    if (entry.holders === 0) revoke(key);
  }
}

function touch(key: string): Entry | null {
  const entry = cache.get(key);
  if (!entry) return null;
  // Re-inserting moves it to the end, which is what makes the eviction order
  // least-recently-used rather than first-in.
  cache.delete(key);
  cache.set(key, entry);
  return entry;
}

export interface DecryptRequest {
  /** The signed URL for the ciphertext. */
  url: string;
  /** The file key sealed with the group key, or null for a legacy plain file. */
  wrappedFileKey: string | null;
  groupId: string;
  /** What the plaintext is, so the blob renders as the right kind of image. */
  contentType?: string;
}

/**
 * Fetch, open, and hand back something renderable — retained.
 *
 * Every successful call must be paired with `release`, or the entry is pinned
 * for the life of the tab.
 *
 * A file stored before encryption existed has no wrapped key, and its URL is
 * already renderable: returned unchanged, and releasing it does nothing, so
 * call sites need no branch of their own.
 */
export async function acquireDecryptedUrl(
  request: DecryptRequest,
): Promise<string> {
  if (!request.wrappedFileKey) return request.url;

  const cached = touch(request.url);
  if (cached) {
    cached.holders += 1;
    return cached.objectUrl;
  }

  const pending = inFlight.get(request.url);
  if (pending) {
    const objectUrl = await pending;
    // The entry exists by now; count this caller against it.
    const entry = cache.get(request.url);
    if (entry) entry.holders += 1;
    return objectUrl;
  }

  const work = fetchAndOpen(request).finally(() =>
    inFlight.delete(request.url),
  );
  inFlight.set(request.url, work);
  return work;
}

/** Stop displaying something. Safe to call with a URL that was never retained. */
export function release(objectUrl: string): void {
  const entry = byObjectUrl.get(objectUrl);
  if (!entry) return;
  if (entry.holders > 0) entry.holders -= 1;
  // Not revoked here: an entry nobody is showing is exactly what the cache is
  // for. It goes when something else needs the room.
  if (entry.holders === 0) evictWhileOver();
}

async function fetchAndOpen(request: DecryptRequest): Promise<string> {
  if (!hasGroupKey(request.groupId)) throw new LockedError();

  const fileKey = await openFileKey(request.groupId, request.wrappedFileKey!);

  /*
   * No credentials: this goes to object storage, not to the API, and sending
   * the session cookie to a third-party host would be both useless and a leak.
   * The signed URL is the whole authorisation.
   */
  let response: Response;
  try {
    response = await fetch(request.url, { credentials: "omit" });
  } catch {
    // A rejected fetch is a network or CORS refusal, never an HTTP error —
    // those resolve with a status. There is deliberately nothing to inspect.
    throw new StorageUnreachableError();
  }

  if (!response.ok) {
    throw new MediaCryptoError(
      `The photograph could not be fetched (${String(response.status)}).`,
    );
  }

  const ciphertext = new Uint8Array(await response.arrayBuffer());
  const plaintext = await decrypt(ciphertext, fileKey);

  const blob = new Blob([plaintext as unknown as BlobPart], {
    type: request.contentType ?? "image/webp",
  });
  const objectUrl = URL.createObjectURL(blob);

  /*
   * Two callers can reach here for the same URL if the first finished and was
   * evicted before the second started. Keep one and revoke the other, rather
   * than leaking whichever lost.
   */
  const existing = cache.get(request.url);
  if (existing) {
    URL.revokeObjectURL(objectUrl);
    existing.holders += 1;
    return existing.objectUrl;
  }

  const entry: Entry = {
    sourceUrl: request.url,
    objectUrl,
    bytes: plaintext.byteLength,
    holders: 1,
  };
  cache.set(request.url, entry);
  byObjectUrl.set(objectUrl, entry);
  heldBytes += plaintext.byteLength;
  evictWhileOver();

  return objectUrl;
}

/**
 * Drop everything, retained or not.
 *
 * Called when the keyring locks: decrypted photographs must not outlive the key
 * that opened them, or signing out would leave them readable in the tab. Any
 * `<img>` still showing one goes blank, which is the intended outcome.
 */
export function clearDecrypted(): void {
  for (const key of [...cache.keys()]) revoke(key);
  byObjectUrl.clear();
  heldBytes = 0;
}

/*
 * Locking takes the decrypted copies with it. Watched from here rather than
 * called from `lock`, so the keyring stays unaware of anything that renders.
 */
let wasUnlocked = isUnlocked();
subscribeKeyring(() => {
  const now = isUnlocked();
  if (wasUnlocked && !now) clearDecrypted();
  wasUnlocked = now;
});

/** Bytes currently held, for tests and for the diagnostics panel. */
export function decryptedBytesHeld(): number {
  return heldBytes;
}

/** Entry count, for tests. */
export function decryptedEntryCount(): number {
  return cache.size;
}
